import { useState } from "react";
import {
  useChangeAdminPasswordMutation,
  useCreateAdminAccountMutation,
} from "../../features/api/appApi";
import AdminSectionCard, {
  AdminButton,
  AdminField,
  AdminFormGrid,
  AdminInput,
  AdminStatusBadge,
} from "./AdminSectionCard";

const AdminSecurityPage = () => {
  const [changeAdminPassword, { isLoading: updatingPassword }] = useChangeAdminPasswordMutation();
  const [createAdminAccount, { isLoading: creatingAdmin }] = useCreateAdminAccountMutation();
  const [passwordNotice, setPasswordNotice] = useState("");
  const [adminNotice, setAdminNotice] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [adminError, setAdminError] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
  });
  const [adminForm, setAdminForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
  });

  return (
    <AdminSectionCard title="Security" subtitle="Update admin password and manage admin accounts">
      <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
        <h3 className="text-sm font-semibold text-slate-800">Update Password</h3>
        <form
          className="mt-3 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setPasswordNotice("");
            setPasswordError("");
            try {
              await changeAdminPassword(passwordForm).unwrap();
              setPasswordForm({ oldPassword: "", newPassword: "" });
              setPasswordNotice("Password updated successfully.");
            } catch (err) {
              setPasswordError(err?.data?.message || "Failed to update password.");
            }
          }}
        >
          <AdminFormGrid>
            <AdminField label="Old password">
              <AdminInput
                type="password"
                placeholder="Old password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                required
              />
            </AdminField>

            <AdminField label="New password">
              <AdminInput
                type="password"
                placeholder="New password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
              />
            </AdminField>
          </AdminFormGrid>

          <AdminButton type="submit" disabled={updatingPassword}>
            {updatingPassword ? "Updating..." : "Update Password"}
          </AdminButton>
        </form>

        <div className="mt-3 space-y-2">
          {passwordNotice ? <AdminStatusBadge tone="green">{passwordNotice}</AdminStatusBadge> : null}
          {passwordError ? <AdminStatusBadge tone="red">{passwordError}</AdminStatusBadge> : null}
        </div>
      </div>

      <div className="my-6 border-t border-slate-200" />

      <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
        <h3 className="text-sm font-semibold text-slate-800">Create Admin Tool</h3>
        <p className="mt-1 text-xs text-slate-500">
          Create a new admin account with email and password. Name and phone are optional.
        </p>

        <form
          className="mt-3 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setAdminNotice("");
            setAdminError("");
            try {
              const payload = {
                email: adminForm.email,
                password: adminForm.password,
                ...(adminForm.name.trim() ? { name: adminForm.name.trim() } : {}),
                ...(adminForm.phone.trim() ? { phone: adminForm.phone.trim() } : {}),
              };
              const res = await createAdminAccount(payload).unwrap();
              setAdminForm({ email: "", password: "", name: "", phone: "" });
              setAdminNotice(res?.message || "New admin created successfully.");
            } catch (err) {
              setAdminError(err?.data?.message || "Failed to create admin.");
            }
          }}
        >
          <AdminFormGrid>
            <AdminField label="Admin email">
              <AdminInput
                type="email"
                placeholder="Admin email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                required
              />
            </AdminField>

            <AdminField label="Admin password">
              <AdminInput
                type="password"
                placeholder="Admin password"
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                required
              />
            </AdminField>

            <AdminField label="Name (optional)">
              <AdminInput
                type="text"
                placeholder="Name"
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
              />
            </AdminField>

            <AdminField label="Phone (optional)">
              <AdminInput
                type="text"
                placeholder="Phone"
                value={adminForm.phone}
                onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}
              />
            </AdminField>
          </AdminFormGrid>

          <AdminButton variant="success" type="submit" disabled={creatingAdmin}>
            {creatingAdmin ? "Creating Admin..." : "Create Admin"}
          </AdminButton>
        </form>

        <div className="mt-3 space-y-2">
          {adminNotice ? <AdminStatusBadge tone="green">{adminNotice}</AdminStatusBadge> : null}
          {adminError ? <AdminStatusBadge tone="red">{adminError}</AdminStatusBadge> : null}
        </div>
      </div>
    </AdminSectionCard>
  );
};

export default AdminSecurityPage;
